import {
  GET_EDIT_ASSIGNMENT_CONFIG_USE_CASE,
  UPDATE_ASSIGNMENT_CONFIG_USE_CASE,
  COMPLETE_ASSIGNMENT_USE_CASE,
  AssignmentConfigSaved,
  AssignmentConfigurations,
  UPDATE_ASSIGNMENT_ACCESS_USE_CASE,
  AssignmentSummary,
  Attendee,
  AttendeeSubmission,
  DELETE_ASSIGNMENT_USE_CASE,
  ExaminationAssignment,
  GET_ASSIGNMENTS_USE_CASE,
  GET_ASSIGNMENT_BY_ID_USE_CASE,
  GET_ASSIGNMENT_CONFIGURATIONS_USE_CASE,
  GET_ATTENDEES_USE_CASE,
  GET_ATTENDEE_SUBMISSIONS_USE_CASE,
  PUBLISH_SCORE_USE_CASE,
  PracticeAssignment,
  UPDATE_EXAMINATION_SUBMISSION_USE_CASE,
  COMPLETE_PRACTICE_ASSIGNMENT_USE_CASE,
  BAN_EXAMINATION_SUBMISSION_USE_CASE,
  CLOSE_EXAMINATION_SUBMISSION_USE_CASE,
} from '@module/assignment';

import { RequestStatus, createReducer } from '@lib/plugin-redux-core';
import { UseCaseError } from '@digibricks/core';
import { SEARCH_USER_USE_CASE, UserSummaryProps } from '@module/user';
import {
  handleCompleteAssignmentUseCase,
  handleGetEditAssignmentConfigUseCase,
  handleUpdateAssignmentConfigUseCase,
  handleDeleteAssignmentUseCase,
  handleGetAssignmentByIdUseCase,
  handleGetAssignmentConfigurationsUseCase,
  handleGetAssignmentUseCase,
  handleGetAttendeeSubmissionUseCase,
  handleGetAttendeesUseCase,
  handlePublishScoreUseCase,
  handleUpdateExaminationSubmissionUseCase,
  handleCompletePracticeAssignmentUseCase,
  handleBanExaminationSubmissionUseCase,
  handleCloseExaminationSubmissionUseCase,
} from './assignment.handler';

export type AssignmentInitialState = {
  closeExaminationSubmissionStatus?: RequestStatus;
  closeExaminationSubmissionId?: string | null;
  closeExaminationSubmissionError?: UseCaseError<unknown>;
  completeAssignmentStatus?: RequestStatus;
  completedAssignment?: { assignmentId: string };
  completeAssignmentError?: UseCaseError<unknown>;
  editAssignment?: PracticeAssignment | ExaminationAssignment;
  editLoadStatus?: RequestStatus;
  configSaveStatus?: RequestStatus;
  configSaved?: AssignmentConfigSaved;
  configError?: UseCaseError<unknown>;
  participationUsers: UserSummaryProps[];
  participationUsersLoading: boolean;
  participationUsersError: unknown;
  updateAccessStatus?: RequestStatus;
  error?: UseCaseError<any>;
  effect: number;
};
export type GetAssignmentsUseCaseState = {
  assignmentResponses: AssignmentSummary[];
  isLoading: boolean;
  getAssignmentSummaryStatus: number;
  assignmentResponseNextToken?: string;
};
export type DeleteAssignmentUseCaseState = {
  deleteAssignmentStatus: number;
  isLoading: boolean;
  deletedAssignmentId: string;
};
export type GetAssignmentConfigurationUseCaseState = {
  assignmentConfigsResponse: AssignmentConfigurations;
  isLoadingConfigs: boolean;
};
export type GetAssignmentByIdUseCaseState = {
  assignmentByIdResponse: PracticeAssignment | ExaminationAssignment;
  getAssignmentByIdStatus: number;
  isLoadingGetById: boolean;
};
export type GetAttendeesUseCaseType = {
  attendeeResponses: Attendee[];
  isLoadingGetAttendees: boolean;
  statusGetAttendee: number;
};
export type GetAttendeeSubmissionsUseCaseState = {
  attendeeSubmissionResponse: AttendeeSubmission;
  isLoadingAttendeeSubmission: boolean;
  statusGetAttendeeSubmission: number;
};
export type PublishScoreUseCaseState = {
  isLoadingPublishScore: boolean;
  publishScoreStatus: number;
};

export type UpdateExaminationSubmissionUseCaseState = {
  updateExaminationSubmissionStatus: RequestStatus;
};

export type BanExaminationSubmissionUseCaseState = {
  banExaminationSubmissionStatus: RequestStatus;
  banExaminationSubmissionId?: string | null;
};

export type CompletePracticeAssignmentUseCaseState = {
  isLoadingCompletePracticeAssignment: boolean;
  completePracticeAssignmentStatus: number;
};

export type AssignmentState = AssignmentInitialState &
  GetAssignmentsUseCaseState &
  DeleteAssignmentUseCaseState &
  GetAssignmentConfigurationUseCaseState &
  GetAssignmentByIdUseCaseState &
  GetAttendeesUseCaseType &
  GetAttendeeSubmissionsUseCaseState &
  PublishScoreUseCaseState &
  CompletePracticeAssignmentUseCaseState &
  UpdateExaminationSubmissionUseCaseState &
  BanExaminationSubmissionUseCaseState;

const initialState: AssignmentInitialState = {
  editLoadStatus: RequestStatus.RESET,
  configSaveStatus: RequestStatus.RESET,
  participationUsers: [],
  participationUsersLoading: false,
  participationUsersError: null,
  error: null,
  effect: 0,
};

export const assignmentReducer = createReducer(initialState, [
  {
    name: COMPLETE_ASSIGNMENT_USE_CASE,
    ...handleCompleteAssignmentUseCase,
  },

  {
    name: GET_EDIT_ASSIGNMENT_CONFIG_USE_CASE,
    ...handleGetEditAssignmentConfigUseCase,
  },
  {
    name: UPDATE_ASSIGNMENT_CONFIG_USE_CASE,
    ...handleUpdateAssignmentConfigUseCase,
  },
  {
    name: SEARCH_USER_USE_CASE,
    executing: (s: AssignmentState) => ({
      ...s,
      participationUsers: [],
      participationUsersLoading: true,
      participationUsersError: null,
    }),
    success: (s: AssignmentState, a: any) => ({
      ...s,
      participationUsers: a.payload,
      participationUsersLoading: false,
    }),
    error: (s: AssignmentState, a: any) => ({
      ...s,
      participationUsersLoading: false,
      participationUsersError: a.error,
    }),
    reset: (s: AssignmentState) => ({
      ...s,
      participationUsers: [],
      participationUsersLoading: false,
      participationUsersError: null,
    }),
  },
  {
    name: UPDATE_ASSIGNMENT_ACCESS_USE_CASE,
    executing: (s: AssignmentState) => ({
      ...s,
      updateAccessStatus: RequestStatus.EXECUTE,
      error: null,
    }),
    success: (s: AssignmentState) => ({ ...s, updateAccessStatus: RequestStatus.SUCCESS }),
    error: (s: AssignmentState, a: any) => ({
      ...s,
      updateAccessStatus: RequestStatus.ERROR,
      error: a.error,
    }),
    reset: (s: AssignmentState) => ({ ...s, updateAccessStatus: RequestStatus.RESET }),
  },
  {
    name: GET_ASSIGNMENTS_USE_CASE,
    ...handleGetAssignmentUseCase,
  },
  {
    name: DELETE_ASSIGNMENT_USE_CASE,
    ...handleDeleteAssignmentUseCase,
  },
  {
    name: GET_ASSIGNMENT_CONFIGURATIONS_USE_CASE,
    ...handleGetAssignmentConfigurationsUseCase,
  },
  {
    name: GET_ASSIGNMENT_BY_ID_USE_CASE,
    ...handleGetAssignmentByIdUseCase,
  },
  {
    name: GET_ATTENDEES_USE_CASE,
    ...handleGetAttendeesUseCase,
  },
  {
    name: GET_ATTENDEE_SUBMISSIONS_USE_CASE,
    ...handleGetAttendeeSubmissionUseCase,
  },
  {
    name: PUBLISH_SCORE_USE_CASE,
    ...handlePublishScoreUseCase,
  },
  {
    name: UPDATE_EXAMINATION_SUBMISSION_USE_CASE,
    ...handleUpdateExaminationSubmissionUseCase,
  },
  {
    name: COMPLETE_PRACTICE_ASSIGNMENT_USE_CASE,
    ...handleCompletePracticeAssignmentUseCase,
  },
  {
    name: CLOSE_EXAMINATION_SUBMISSION_USE_CASE,
    ...handleCloseExaminationSubmissionUseCase,
  },
  {
    name: BAN_EXAMINATION_SUBMISSION_USE_CASE,
    ...handleBanExaminationSubmissionUseCase,
  },
]);
